import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../utils/api';
import { MealCataloguePicker, AllergenSuggestPanel } from './MealCataloguePicker';

/**
 * MealsManager
 * ============
 * A single component that renders three Admin sub-views and one Parent view:
 *
 *   • mode='admin-library'  — CRUD the per-org meal library (name,
 *     ingredients, allergens, dietary tags).
 *   • mode='admin-planner'  — Build / publish a 2/3/4-week menu plan
 *     by clicking each cell of a (cycle_weeks × 7 days × 5 slots) grid.
 *   • mode='admin-today'    — See what is being served today and apply
 *     last-minute overrides (chef sick, special event).
 *   • mode='parent'         — Weekly view, with per-child allergy banners
 *     served by the backend (UK-14 set intersection).
 *
 * Why one component? Most of the shared state (meal cache, slot order,
 * label maps) is the same across modes, so colocating prevents drift.
 */

const SLOTS = ['breakfast', 'morning_snack', 'lunch', 'afternoon_snack', 'tea'];
const SLOT_LABELS = {
  breakfast: '🥣 Breakfast',
  morning_snack: '🍎 AM Snack',
  lunch: '🍽️ Lunch',
  afternoon_snack: '🍓 PM Snack',
  tea: '🥪 Tea',
};
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const UK14 = [
  'gluten', 'crustaceans', 'eggs', 'fish', 'peanuts', 'soya',
  'milk', 'nuts', 'celery', 'mustard', 'sesame', 'sulphites',
  'lupin', 'molluscs',
];
const DIETARY_TAGS = [
  'halal', 'kosher', 'vegetarian', 'vegan',
  'lactose_free', 'gluten_free', 'nut_free', 'pork_free',
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const startOfWeekISO = (d = new Date()) => {
  const date = new Date(d);
  const dow = (date.getDay() + 6) % 7; // 0=Mon
  date.setDate(date.getDate() - dow);
  return date.toISOString().slice(0, 10);
};
const fmtDayHeader = (iso) => {
  const d = new Date(iso);
  return `${DAY_LABELS[(d.getDay() + 6) % 7]} ${d.getDate()}/${d.getMonth() + 1}`;
};

// ---------- Meal Library ----------
const MealLibrary = () => {
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  // Catalogue picker (📚 platform-wide common meals).
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMeal());
  const [err, setErr] = useState('');

  function emptyMeal() {
    return {
      name: '', category: 'main', description: '',
      ingredients: '', allergens: [], dietary_tags: [],
      notes_for_staff: '',
    };
  }

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setMeals(await api.getMeals());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => { setEditing(null); setForm(emptyMeal()); setShowModal(true); setErr(''); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name || '',
      category: m.category || 'main',
      description: m.description || '',
      ingredients: (m.ingredients || []).join(', '),
      allergens: m.allergens || [],
      dietary_tags: m.dietary_tags || [],
      notes_for_staff: m.notes_for_staff || '',
    });
    setShowModal(true);
    setErr('');
  };

  const toggleArr = (key, val) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { setErr('Name is required'); return; }
    const payload = {
      name: form.name.trim(),
      category: form.category,
      description: form.description,
      ingredients: form.ingredients.split(',').map(s => s.trim()).filter(Boolean),
      allergens: form.allergens,
      dietary_tags: form.dietary_tags,
      notes_for_staff: form.notes_for_staff,
    };
    try {
      if (editing) await api.updateMeal(editing._id, payload);
      else await api.createMeal(payload);
      setShowModal(false);
      reload();
    } catch (e2) { setErr(e2.message); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Archive "${m.name}"? It will stop appearing in the menu planner.`)) return;
    try { await api.deleteMeal(m._id); reload(); } catch (e) { alert(e.message); }
  };

  if (loading) return <div className="card" data-testid="meals-loading">Loading meals…</div>;

  return (
    <div data-testid="meal-library">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '0.75rem', flexWrap: 'wrap' }}>
        <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))' }}>
          Add the dishes your kitchen prepares, with ingredients and allergens. The menu planner picks from this library.
        </p>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button
            className="btn btn-ghost"
            onClick={() => setShowCatalogue(true)}
            data-testid="pick-from-library-btn"
            title="Browse 50+ common UK childcare & school-club meals with allergens pre-tagged"
          >
            📚 Pick from library
          </button>
          <button className="btn btn-primary" onClick={openCreate} data-testid="add-meal-btn">+ Add meal</button>
        </div>
      </div>

      <MealCataloguePicker
        open={showCatalogue}
        onClose={() => setShowCatalogue(false)}
        onCloned={() => reload()}
      />

      {meals.length === 0 ? (
        <div className="card empty-state" data-testid="meals-empty">
          <div style={{ fontSize: '2rem' }}>🍽️</div>
          <h3>No meals yet</h3>
          <p>Start by adding a few dishes — Cottage Pie, Spag Bol, Veggie Curry…</p>
          <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
            💡 Tip: tap <strong>📚 Pick from library</strong> to import common UK childcare meals in one click — allergens come pre-tagged.
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Allergens</th>
                  <th>Dietary</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {meals.map((m) => (
                  <tr key={m._id} data-testid={`meal-row-${m._id}`}>
                    <td><strong>{m.name}</strong>
                      {m.description && (
                        <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                          {m.description}
                        </div>
                      )}
                    </td>
                    <td>{m.category || '—'}</td>
                    <td>
                      {(m.allergens || []).map((a, i) => (
                        <span key={i} className="badge" style={{
                          background: 'hsl(0 70% 92%)', color: 'hsl(0 70% 30%)',
                          marginRight: 4, padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.7rem',
                        }}>{a}</span>
                      ))}
                      {(m.allergens || []).length === 0 && <span style={{ color: 'hsl(var(--muted-foreground))' }}>—</span>}
                    </td>
                    <td>
                      {(m.dietary_tags || []).map((t, i) => (
                        <span key={i} className="badge" style={{
                          background: 'hsl(140 50% 92%)', color: 'hsl(140 60% 25%)',
                          marginRight: 4, padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.7rem',
                        }}>{t}</span>
                      ))}
                    </td>
                    <td>
                      <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }}
                        onClick={() => openEdit(m)} data-testid={`meal-edit-${m._id}`}>
                        Edit
                      </button>
                      <button className="btn btn-ghost" style={{ fontSize: '0.8rem', color: 'hsl(0 70% 35%)' }}
                        onClick={() => remove(m)} data-testid={`meal-delete-${m._id}`}>
                        Archive
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)} data-testid="meal-modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }} data-testid="meal-modal">
            <div className="modal-header">
              <h2 style={{ margin: 0 }}>{editing ? 'Edit meal' : 'Add meal'}</h2>
            </div>
            <form onSubmit={submit} autoComplete="off">
              <div className="form-group">
                <label>Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="meal-name" />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="meal-category">
                  <option value="main">Main</option>
                  <option value="snack">Snack</option>
                  <option value="drink">Drink</option>
                  <option value="dessert">Dessert</option>
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Short note shown to parents (e.g. 'Beef mince with mash topping')."
                  data-testid="meal-description" />
                {/* AI allergen assistant — fires automatically on
                    debounced name typing, falls back to a heuristic
                    when the LLM is unavailable. Suggestions are always
                    advisory; the user picks which chips to accept. */}
                <AllergenSuggestPanel
                  name={form.name}
                  description={form.description}
                  currentAllergens={form.allergens}
                  currentDietaryTags={form.dietary_tags}
                  onApply={({ allergens, dietary_tags }) => setForm((f) => ({
                    ...f,
                    allergens: allergens || f.allergens,
                    dietary_tags: dietary_tags || f.dietary_tags,
                  }))}
                />
              </div>
              <div className="form-group">
                <label>Ingredients (comma separated)</label>
                <input value={form.ingredients}
                  onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
                  placeholder="beef, potato, milk, onion" data-testid="meal-ingredients" />
              </div>
              <div className="form-group">
                <label>Allergens (UK 14 priority list)</label>
                <small style={{ color: 'hsl(var(--muted-foreground))', display: 'block', marginBottom: 4 }}>
                  Tick everything this meal contains. Parents whose children have matching allergies get an automatic warning.
                </small>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }} data-testid="meal-allergens">
                  {UK14.map((a) => {
                    const on = form.allergens.includes(a);
                    return (
                      <button type="button" key={a}
                        className={on ? 'btn btn-primary' : 'btn btn-ghost'}
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem',
                          ...(on ? { background: 'hsl(0 70% 50%)', borderColor: 'hsl(0 70% 50%)' } : {}) }}
                        onClick={() => toggleArr('allergens', a)}
                        data-testid={`meal-allergen-${a}`}>
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="form-group">
                <label>Dietary tags</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                  {DIETARY_TAGS.map((t) => {
                    const on = form.dietary_tags.includes(t);
                    return (
                      <button type="button" key={t}
                        className={on ? 'btn btn-primary' : 'btn btn-ghost'}
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
                        onClick={() => toggleArr('dietary_tags', t)}
                        data-testid={`meal-tag-${t}`}>
                        {t.replace('_', ' ')}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="form-group">
                <label>Notes for kitchen staff</label>
                <textarea rows={2} value={form.notes_for_staff}
                  onChange={(e) => setForm({ ...form, notes_for_staff: e.target.value })}
                  data-testid="meal-notes" />
              </div>
              {err && <div style={{ color: 'hsl(0 70% 35%)', marginBottom: 6 }} data-testid="meal-error">{err}</div>}
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)} data-testid="meal-cancel">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="meal-submit">
                  {editing ? 'Save changes' : 'Add meal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Menu Planner ----------
const MenuPlanner = () => {
  const [meals, setMeals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePlan, setActivePlan] = useState(null);
  const [grid, setGrid] = useState({}); // local edit copy of meals_by_week_day_slot
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', cycle_weeks: 2, start_date: startOfWeekISO(),
  });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([api.getMeals(), api.getMenuPlans()]);
      setMeals(m);
      setPlans(p);
      const published = p.find(x => x.status === 'published') || p[0] || null;
      if (published) {
        setActivePlan(published);
        setGrid(published.meals_by_week_day_slot || {});
      } else {
        setActivePlan(null);
        setGrid({});
      }
      setDirty(false);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const mealById = useMemo(() => {
    const m = {}; meals.forEach(x => { m[x._id] = x; }); return m;
  }, [meals]);

  const setCell = (week, day, slot, mealId) => {
    setGrid((g) => {
      const next = JSON.parse(JSON.stringify(g));
      next[week] = next[week] || {};
      next[week][day] = next[week][day] || {};
      if (mealId) next[week][day][slot] = mealId;
      else delete next[week][day][slot];
      return next;
    });
    setDirty(true);
  };

  const saveGrid = async () => {
    if (!activePlan) return;
    setSaving(true);
    try {
      await api.updateMenuPlan(activePlan._id, { meals_by_week_day_slot: grid });
      setDirty(false);
      alert('Menu saved.');
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const publishToggle = async () => {
    if (!activePlan) return;
    const nextStatus = activePlan.status === 'published' ? 'draft' : 'published';
    if (nextStatus === 'published' && !window.confirm('Publish this plan? Any other published plan will be archived. Parents will see this menu.')) return;
    try {
      await api.updateMenuPlan(activePlan._id, { status: nextStatus });
      reload();
    } catch (e) { alert(e.message); }
  };

  const switchPlan = (id) => {
    const p = plans.find(x => x._id === id);
    if (!p) return;
    if (dirty && !window.confirm('Switch plan? Unsaved changes will be lost.')) return;
    setActivePlan(p);
    setGrid(p.meals_by_week_day_slot || {});
    setDirty(false);
  };

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    if (!createForm.name.trim()) return;
    try {
      await api.createMenuPlan({
        name: createForm.name.trim(),
        cycle_weeks: Number(createForm.cycle_weeks),
        start_date: new Date(createForm.start_date).toISOString(),
        meals_by_week_day_slot: {},
      });
      setShowCreate(false);
      setCreateForm({ name: '', cycle_weeks: 2, start_date: startOfWeekISO() });
      reload();
    } catch (e2) { alert(e2.message); }
  };

  const deletePlan = async () => {
    if (!activePlan) return;
    if (!window.confirm(`Archive "${activePlan.name}"?`)) return;
    try { await api.deleteMenuPlan(activePlan._id); reload(); } catch (e) { alert(e.message); }
  };

  if (loading) return <div className="card">Loading planner…</div>;

  return (
    <div data-testid="menu-planner">
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <select
          value={activePlan?._id || ''}
          onChange={(e) => switchPlan(e.target.value)}
          data-testid="plan-picker"
          disabled={plans.length === 0}
          style={{ minWidth: 220 }}
        >
          {plans.length === 0 && <option value="">— No plans yet —</option>}
          {plans.map(p => (
            <option key={p._id} value={p._id}>
              {p.name} · {p.cycle_weeks}w · {p.status}
            </option>
          ))}
        </select>
        <button className="btn btn-ghost" onClick={() => setShowCreate(true)} data-testid="add-plan-btn">
          + New plan
        </button>
        {activePlan && (
          <>
            <button className="btn btn-primary" onClick={saveGrid} disabled={!dirty || saving} data-testid="save-plan-btn">
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
            <button className="btn btn-ghost" onClick={publishToggle} data-testid="publish-plan-btn">
              {activePlan.status === 'published' ? 'Unpublish' : 'Publish'}
            </button>
            <button className="btn btn-ghost" onClick={deletePlan} style={{ color: 'hsl(0 70% 35%)' }} data-testid="delete-plan-btn">
              Archive
            </button>
            <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
              Cycle: {activePlan.cycle_weeks} week{activePlan.cycle_weeks === 1 ? '' : 's'}, anchored at{' '}
              {new Date(activePlan.start_date).toLocaleDateString()}
            </span>
          </>
        )}
      </div>

      {!activePlan ? (
        <div className="card empty-state" data-testid="planner-empty">
          <div style={{ fontSize: '2rem' }}>📅</div>
          <h3>No menu plan yet</h3>
          <p>Create your first rotation. Pick 2, 3 or 4 weeks; you can change it later.</p>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New plan</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {Array.from({ length: activePlan.cycle_weeks }).map((_, w) => (
            <div className="card" key={w} style={{ padding: '0.75rem', overflowX: 'auto' }} data-testid={`planner-week-${w}`}>
              <h3 style={{ margin: '0 0 0.5rem 0' }}>Week {w + 1}</h3>
              <table style={{ width: '100%', minWidth: 720 }}>
                <thead>
                  <tr>
                    <th style={{ width: 110, textAlign: 'left' }}>Slot</th>
                    {DAY_LABELS.map((d, i) => (
                      <th key={i}>{d}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SLOTS.map((slot) => (
                    <tr key={slot}>
                      <td style={{ fontWeight: 600 }}>{SLOT_LABELS[slot]}</td>
                      {DAY_LABELS.map((_, day) => {
                        const mealId = grid?.[w]?.[day]?.[slot];
                        const meal = mealId ? mealById[mealId] : null;
                        return (
                          <td key={day} style={{ padding: 4 }}>
                            <select
                              value={mealId || ''}
                              onChange={(e) => setCell(w, day, slot, e.target.value)}
                              data-testid={`cell-${w}-${day}-${slot}`}
                              style={{ width: '100%', fontSize: '0.8rem', padding: '0.25rem' }}
                            >
                              <option value="">— none —</option>
                              {meals.map(m => (
                                <option key={m._id} value={m._id}>{m.name}</option>
                              ))}
                            </select>
                            {meal && (meal.allergens || []).length > 0 && (
                              <div style={{ fontSize: '0.65rem', color: 'hsl(0 70% 35%)', marginTop: 2 }}>
                                ⚠ {meal.allergens.join(', ')}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)} data-testid="plan-create-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="plan-create-modal">
            <div className="modal-header"><h2 style={{ margin: 0 }}>New menu plan</h2></div>
            <form onSubmit={submitCreate} autoComplete="off">
              <div className="form-group">
                <label>Name</label>
                <input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="e.g. Spring Cycle" required data-testid="plan-name" />
              </div>
              <div className="form-group">
                <label>Rotation length</label>
                <select value={createForm.cycle_weeks}
                  onChange={(e) => setCreateForm({ ...createForm, cycle_weeks: e.target.value })}
                  data-testid="plan-cycle">
                  <option value={2}>2 weeks</option>
                  <option value={3}>3 weeks</option>
                  <option value={4}>4 weeks</option>
                </select>
              </div>
              <div className="form-group">
                <label>Start date (Monday recommended)</label>
                <input type="date" value={createForm.start_date}
                  onChange={(e) => setCreateForm({ ...createForm, start_date: e.target.value })}
                  data-testid="plan-start" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)} data-testid="plan-cancel">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="plan-submit">Create plan</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Today / Override view ----------
const TodayView = ({ allowOverride = true }) => {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meals, setMeals] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m] = await Promise.all([api.getActiveMenu(date), api.getMeals()]);
      setData(d); setMeals(m);
    } finally { setLoading(false); }
  }, [date]);
  useEffect(() => { reload(); }, [reload]);

  const setOverride = async (slot, mealId, reason) => {
    if (!data?.plan?._id) return;
    try {
      await api.setMenuOverride(data.plan._id, {
        date: new Date(date).toISOString(),
        slot,
        meal_id: mealId || null,
        reason: reason || null,
      });
      reload();
    } catch (e) { alert(e.message); }
  };

  const clearOverride = async (slot) => {
    if (!data?.plan?._id) return;
    try {
      await api.clearMenuOverride(data.plan._id, new Date(date).toISOString(), slot);
      reload();
    } catch (e) { alert(e.message); }
  };

  if (loading) return <div className="card">Loading…</div>;
  if (!data?.plan) {
    return (
      <div className="card empty-state" data-testid="today-empty">
        <div style={{ fontSize: '2rem' }}>📅</div>
        <h3>No published menu</h3>
        <p>Publish a plan in the Planner tab to see daily meals here.</p>
      </div>
    );
  }

  return (
    <div data-testid="today-view">
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ marginRight: '0.5rem', fontWeight: 600 }}>Date:</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="today-date" />
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 140 }}>Slot</th>
                <th>Meal</th>
                <th>Allergens</th>
                <th>Override</th>
                {allowOverride && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {data.slots.map((s) => (
                <tr key={s.slot} data-testid={`today-slot-${s.slot}`}>
                  <td style={{ fontWeight: 600 }}>{SLOT_LABELS[s.slot]}</td>
                  <td>{s.meal ? <strong>{s.meal.name}</strong> : <em style={{ color: 'hsl(var(--muted-foreground))' }}>(none)</em>}</td>
                  <td>
                    {s.meal && (s.meal.allergens || []).map((a, i) => (
                      <span key={i} className="badge" style={{
                        background: 'hsl(0 70% 92%)', color: 'hsl(0 70% 30%)',
                        marginRight: 4, padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.7rem',
                      }}>{a}</span>
                    ))}
                  </td>
                  <td>{s.is_override ? (s.override_reason || 'Override') : '—'}</td>
                  {allowOverride && (
                    <td>
                      <select
                        value={s.is_override ? (s.meal?._id || '__none__') : '__cycle__'}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '__cycle__') clearOverride(s.slot);
                          else if (v === '__none__') {
                            const reason = prompt('Reason? (e.g. Chef sick)', s.override_reason || '');
                            if (reason !== null) setOverride(s.slot, null, reason);
                          } else {
                            const reason = prompt('Reason? (e.g. Special event)', s.override_reason || '');
                            if (reason !== null) setOverride(s.slot, v, reason);
                          }
                        }}
                        data-testid={`today-override-${s.slot}`}
                        style={{ fontSize: '0.8rem' }}
                      >
                        <option value="__cycle__">— Use rotation —</option>
                        <option value="__none__">⚠ Skip / no meal</option>
                        {meals.map(m => (
                          <option key={m._id} value={m._id}>Override → {m.name}</option>
                        ))}
                      </select>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------- Parent week view ----------
const ParentWeekView = () => {
  const [weekStart, setWeekStart] = useState(startOfWeekISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.getWeekMenu(weekStart));
    } finally { setLoading(false); }
  }, [weekStart]);
  useEffect(() => { reload(); }, [reload]);

  const shift = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  if (loading) return <div className="card">Loading menu…</div>;
  if (!data || !data.days?.length) return <div className="card">No menu published yet.</div>;
  const noPlan = data.days.every(d => !d.plan);

  return (
    <div data-testid="parent-week">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost" onClick={() => shift(-1)} data-testid="week-prev">← Prev</button>
        <strong>Week of {new Date(weekStart).toLocaleDateString()}</strong>
        <button className="btn btn-ghost" onClick={() => shift(1)} data-testid="week-next">Next →</button>
        <button className="btn btn-ghost" onClick={() => setWeekStart(startOfWeekISO())} data-testid="week-this">This week</button>
      </div>

      {noPlan ? (
        <div className="card empty-state">
          <div style={{ fontSize: '2rem' }}>🍽️</div>
          <h3>Menu not yet published</h3>
          <p>Your provider hasn't published a weekly menu. Check back later.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0.75rem' }}>
          {data.days.map((day) => (
            <div className="card" key={day.date} style={{ padding: '0.75rem' }} data-testid={`parent-day-${day.date}`}>
              <h3 style={{ margin: '0 0 0.5rem 0' }}>{fmtDayHeader(day.date)}</h3>
              {SLOTS.map((slot) => {
                const s = day.slots.find(x => x.slot === slot);
                if (!s) return null;
                const hasAlert = (s.allergy_alerts || []).length > 0;
                return (
                  <div key={slot} style={{
                    padding: '0.4rem 0.5rem', borderRadius: 6, marginBottom: 4,
                    background: hasAlert ? 'hsl(0 70% 95%)' : 'hsl(var(--muted) / 0.4)',
                    border: hasAlert ? '1px solid hsl(0 70% 75%)' : '1px solid transparent',
                  }} data-testid={`parent-slot-${day.date}-${slot}`}>
                    <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>{SLOT_LABELS[slot]}</div>
                    <div style={{ fontWeight: 600 }}>
                      {s.meal ? s.meal.name : <em style={{ color: 'hsl(var(--muted-foreground))', fontWeight: 400 }}>(none)</em>}
                    </div>
                    {s.meal?.description && (
                      <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>{s.meal.description}</div>
                    )}
                    {(s.meal?.allergens || []).length > 0 && (
                      <div style={{ fontSize: '0.7rem', marginTop: 2 }}>
                        Contains: {s.meal.allergens.join(', ')}
                      </div>
                    )}
                    {hasAlert && (
                      <div style={{ fontSize: '0.72rem', color: 'hsl(0 70% 35%)', marginTop: 2, fontWeight: 600 }}>
                        ⚠ {s.allergy_alerts.map(a => `${a.child_name} is allergic to ${a.matched.join(', ')}`).join(' · ')}
                      </div>
                    )}
                    {s.is_override && (
                      <div style={{ fontSize: '0.7rem', color: 'hsl(40 80% 30%)', marginTop: 2 }}>
                        ↻ Override{s.override_reason ? ` — ${s.override_reason}` : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Top-level dispatcher ----------
export const MealsManager = ({ mode = 'admin-library' }) => {
  if (mode === 'admin-library') return <MealLibrary />;
  if (mode === 'admin-planner') return <MenuPlanner />;
  if (mode === 'admin-today') return <TodayView allowOverride />;
  if (mode === 'parent') return <ParentWeekView />;
  return null;
};

export default MealsManager;
